import * as Sentry from '@sentry/react';
import { useState, useEffect } from 'react';
import {
  AlertTriangle,
  FileDown,
  BarChart,
  Rss,
  FileText,
  FileType,
  FileSpreadsheet,
  FileCode,
  Loader2,
  Pencil,
  Trash2,
} from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Dialog, DialogTrigger, DialogTitle, DialogHeader, DialogContent } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from '@/hooks/use-toast';
import { subHours } from 'date-fns';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import {
  GetDevicesByIdApiResponse,
  GetDevicesByIdStatsApiResponse,
  useDeleteDevicesByDeviceIdSensorsAndSensorIdMutation,
  usePatchDevicesByDeviceIdSensorsAndSensorIdMutation,
  usePostDevicesByDeviceIdSensorsMutation,
  useLazyGetExportSensorDataQuery,
} from '@/services';
import { Link } from '@tanstack/react-router';

interface SensorsTabProps {
  deviceId: string;
  sensors?: GetDevicesByIdApiResponse['data']['sensors'];
  refetch: () => void;
  sensorStats?: GetDevicesByIdStatsApiResponse['data']['sensor_stats'];
  isLoading: boolean;
  isFetching: boolean;
  timeRange: string;
  setTimeRange: (range: string) => void;
  aggregation: string;
  setAggregation: (agg: string) => void;
  timeRangeOptions: { value: string; label: string }[];
}

// --- Static lists for standard sensor types and units ---
const STANDARD_SENSOR_TYPES = [
  'temperature',
  'humidity',
  'pressure',
  'light',
  'motion',
  'proximity',
  'sound',
  'gas',
  'smoke',
  'vibration',
  'voltage',
  'current',
  'power',
  'energy',
  'ph',
  'co2',
  'water_level',
  'weight',
  'distance',
  'custom',
] as const;

const STANDARD_UNITS = [
  '°C',
  '°F',
  '%',
  'Pa',
  'lux',
  'm/s²',
  'dB',
  'ppm',
  'mg/m³',
  'V',
  'A',
  'W',
  'kWh',
  'pH',
  'cm',
  'mm',
  'kg',
  'g',
  'm',
  'custom',
] as const;

// --- Zod schema ---
const addSensorFormSchema = z.object({
  name: z.string().min(1, 'Sensor name is required'),
  type: z.string().min(1, 'Sensor type is required'),
  unit: z.string().nullable(),
  description: z.string().nullable(),
});
type AddSensorFormValues = z.infer<typeof addSensorFormSchema>;

// --- AddSensor component ---
function AddSensor({ deviceId, refetch }: { deviceId: string; refetch: () => void }) {
  const [addSensorModalOpen, setAddSensorModalOpen] = useState(false);
  const [customType, setCustomType] = useState('');
  const [customUnit, setCustomUnit] = useState('');
  const [typeInput, setTypeInput] = useState('');
  const [unitInput, setUnitInput] = useState('');
  const [addSensorApi, { isLoading }] = usePostDevicesByDeviceIdSensorsMutation();

  const form = useForm<AddSensorFormValues>({
    resolver: zodResolver(addSensorFormSchema),
    defaultValues: {
      name: '',
      type: '',
      unit: '',
      description: '',
    },
  });

  // Filter standard types/units based on input for autocomplete
  const filteredTypes = typeInput
    ? STANDARD_SENSOR_TYPES.filter((t) => t.toLowerCase().includes(typeInput.toLowerCase()))
    : STANDARD_SENSOR_TYPES;
  const filteredUnits = unitInput
    ? STANDARD_UNITS.filter((u) => u.toLowerCase().includes(unitInput.toLowerCase()))
    : STANDARD_UNITS;

  async function onSubmit(values: AddSensorFormValues) {
    try {
      const typeToSend = customType || values.type;
      const unitToSend = customUnit || values.unit;
      const res = await addSensorApi({
        deviceId: deviceId,
        body: {
          ...values,
          type: typeToSend,
          unit: unitToSend,
        },
      });
      if (!res.data?.success) {
        throw res.error;
      }

      toast({
        variant: 'success',
        title: `Sensor "${values.name}" added successfully`,
        description: '',
      });

      refetch();
      setAddSensorModalOpen(false);
      form.reset();
      setCustomType('');
      setCustomUnit('');
      setTypeInput('');
      setUnitInput('');
    } catch (error: unknown) {
      Sentry.captureException(error);
      toast({
        variant: 'error',
        title: 'Failed to add sensor',
        description: '',
      });
    }
  }

  return (
    <Dialog open={addSensorModalOpen} onOpenChange={setAddSensorModalOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          + Add Sensor
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a New Sensor</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sensor Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Living Room Temperature" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Sensor Type with autocomplete and custom entry */}
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sensor Type</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        placeholder="e.g. temperature, humidity, custom type..."
                        value={customType || field.value}
                        onChange={(e) => {
                          setTypeInput(e.target.value);
                          setCustomType(e.target.value);
                          field.onChange(e.target.value);
                        }}
                        autoComplete="off"
                        list="sensor-type-list"
                        required
                      />
                      <datalist id="sensor-type-list">
                        {filteredTypes.map((type) => (
                          <option key={type} value={type} />
                        ))}
                      </datalist>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Sensor Unit with autocomplete and custom entry */}
            <FormField
              control={form.control}
              name="unit"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Unit</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        placeholder="e.g. °C, %, custom unit..."
                        value={customUnit || field.value || ''}
                        onChange={(e) => {
                          setUnitInput(e.target.value);
                          setCustomUnit(e.target.value);
                          field.onChange(e.target.value);
                        }}
                        autoComplete="off"
                        list="sensor-unit-list"
                      />
                      <datalist id="sensor-unit-list">
                        {filteredUnits.map((unit) => (
                          <option key={unit} value={unit} />
                        ))}
                      </datalist>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="Short description" {...field} value={field.value || ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Adding...' : 'Add Sensor'}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

type Sensor = {
  id: string;
  name: string;
  type: string;
  unit: string | null;
  description: string | null;
  latest_data?: {
    value: number;
    unit: string | null;
    timestamp: string;
  } | null;
};

const patchSensorFormSchema = z.object({
  name: z.string().min(1, 'New sensor name'),
  description: z.string().nullable(),
});
type PatchSensorFormValues = z.infer<typeof patchSensorFormSchema>;

function DeleteSensor({ deviceId, sensor, refetch }: { deviceId: string; sensor: Sensor; refetch: () => void }) {
  const [deleteSensorModalOpen, setDeleteSensorModalOpen] = useState(false);
  const [deleteSensorApi, { isLoading }] = useDeleteDevicesByDeviceIdSensorsAndSensorIdMutation();

  async function onDelete() {
    try {
      await deleteSensorApi({ deviceId, sensorId: sensor.id }).unwrap();

      toast({
        variant: 'success',
        title: `Sensor "${sensor.name}" deleted successfully`,
        description: '',
      });

      refetch();
      setDeleteSensorModalOpen(false);
    } catch (error: unknown) {
      Sentry.captureException(error);
      toast({
        variant: 'error',
        title: 'Failed to delete sensor',
        description: '',
      });
    }
  }
  return (
    <>
      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setDeleteSensorModalOpen(true)}>
        <Trash2 className="h-4 w-4" />
      </Button>

      <Dialog open={deleteSensorModalOpen} onOpenChange={setDeleteSensorModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Sensor</DialogTitle>
          </DialogHeader>
          <p>Are you sure you want to delete this sensor? This action cannot be undone.</p>
          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={() => setDeleteSensorModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" className="ml-2" onClick={onDelete} disabled={isLoading}>
              {isLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function UpdateSensor({ deviceId, sensor, refetch }: { deviceId: string; sensor: Sensor; refetch: () => void }) {
  const [updateSensorModalOpen, setUpdateSensorModalOpen] = useState(false);
  const [updateSensorApi, { isLoading }] = usePatchDevicesByDeviceIdSensorsAndSensorIdMutation();

  const form = useForm<PatchSensorFormValues>({
    resolver: zodResolver(patchSensorFormSchema),
    defaultValues: {
      name: sensor.name || '',
      description: sensor.description || '',
    },
  });

  useEffect(() => {
    if (updateSensorModalOpen) {
      form.reset({
        name: sensor.name || '',
        description: sensor.description || '',
      });
    }
  }, [updateSensorModalOpen, sensor, form]);

  async function onSubmit(values: PatchSensorFormValues) {
    try {
      await updateSensorApi({
        deviceId,
        sensorId: sensor.id,
        body: values,
      }).unwrap();

      toast({
        variant: 'success',
        title: `Sensor "${values.name}" updated successfully`,
        description: '',
      });

      refetch();
      setUpdateSensorModalOpen(false);
    } catch (error: unknown) {
      Sentry.captureException(error);
      toast({
        variant: 'error',
        title: 'Failed to update sensor',
        description: '',
      });
    }
  }

  return (
    <>
      <Button size="sm" variant="ghost" onClick={() => setUpdateSensorModalOpen(true)}>
        <Pencil className="h-4 w-4" />
      </Button>

      <Dialog open={updateSensorModalOpen} onOpenChange={setUpdateSensorModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Sensor</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Sensor Name</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. Living Room Temperature" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Input placeholder="Short description" {...field} value={field.value || ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? 'Updating...' : 'Update Sensor'}
              </Button>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function SensorsTab({
  deviceId,
  sensors = [],
  sensorStats = [],
  isLoading,
  isFetching,
  timeRange,
  setTimeRange,
  aggregation,
  setAggregation,
  timeRangeOptions,
  refetch,
}: SensorsTabProps) {
  console.log('SensorsTab', { deviceId, sensors, sensorStats, isLoading, timeRange, aggregation });
  const [selectedSensorType, setSelectedSensorType] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [searchTerm, setSearchTerm] = useState('');

  // Get unique sensor types
  const sensorTypes = Array.from(new Set(sensors.map((sensor) => sensor.type)));

  // Filter sensors by type and search term
  const filteredSensors = sensors.filter((sensor) => {
    const matchesType = selectedSensorType === 'all' ? true : sensor.type === selectedSensorType;
    const matchesSearch = searchTerm
      ? sensor.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sensor.type?.toLowerCase().includes(searchTerm.toLowerCase())
      : true;
    return matchesType && matchesSearch;
  });

  // Selected time range label
  const selectedTimeRangeLabel =
    timeRangeOptions.find((option) => option.value === timeRange)?.label || `Last ${timeRange} hours`;

  const [triggerExport] = useLazyGetExportSensorDataQuery();

  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async (format: 'json' | 'csv' | 'pdf' | 'xlsx') => {
    const startDate = subHours(new Date(), parseInt(timeRange)).toISOString();
    const endDate = new Date().toISOString();

    const filename = `sensor-data-${startDate.replace(/:/g, '-')}TO${endDate.replace(/:/g, '-')}.${format}`;
    try {
      setIsExporting(true);

      const result = await triggerExport({
        deviceIds: deviceId || '',
        sensorTypes: selectedSensorType !== 'all' ? selectedSensorType : '',
        startDate: startDate,
        endDate: endDate,
        format,
      }).unwrap();

      const mimeTypes: Record<string, string> = {
        csv: 'text/csv',
        json: 'application/json',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        pdf: 'application/pdf',
      };

      const blob = new Blob([result], { type: mimeTypes[format] });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();

      URL.revokeObjectURL(url);
    } catch (err) {
      // Handle error
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6 pt-4">
      {/* Enhanced Filters */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col md:flex-row justify-between gap-4">
          {/* Search box */}
          <div className="relative w-full md:w-[300px] flex items-center gap-4">
            <Input
              placeholder="Search sensors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
            <AddSensor deviceId={deviceId} refetch={refetch} />
          </div>

          {/* View mode toggle */}
          <div className="flex gap-2 items-center">
            <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'grid' | 'table')}>
              <TabsList>
                <TabsTrigger value="grid">Grid</TabsTrigger>
                <TabsTrigger value="table">Table</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>

        <div className="flex flex-wrap justify-between gap-4">
          {/* Sensor type badges */}
          <div className="flex flex-wrap gap-2">
            <Badge
              variant={selectedSensorType === 'all' ? 'default' : 'outline'}
              className="cursor-pointer"
              onClick={() => setSelectedSensorType('all')}
            >
              All Types
            </Badge>
            {sensorTypes.map((type) => (
              <Badge
                key={type}
                variant={selectedSensorType === type ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setSelectedSensorType(type)}
              >
                {type}
              </Badge>
            ))}
          </div>

          <div className="flex gap-2">
            {/* Fetching Indicator */}
            {isFetching && (
              <div className="flex items-center">
                <Skeleton className="h-4 w-4 animate-pulse bg-primary" />
              </div>
            )}
            {/* Time range selector */}
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Time Range" />
              </SelectTrigger>
              <SelectContent>
                {timeRangeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Aggregation selector */}
            <Select value={aggregation} onValueChange={setAggregation}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="View" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="latest">Latest Values</SelectItem>
                <SelectItem value="average">Average</SelectItem>
                <SelectItem value="min">Minimum</SelectItem>
                <SelectItem value="max">Maximum</SelectItem>
              </SelectContent>
            </Select>

            {/* Export Sensor data */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-[180px]" disabled={isExporting}>
                  {isExporting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <FileDown className="mr-2 h-4 w-4" />
                      Export
                    </>
                  )}
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent className="w-[180px]" align="end">
                <DropdownMenuItem onClick={() => handleExport('pdf')} disabled={isExporting}>
                  <FileType className="mr-2 h-4 w-4" />
                  pdf
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('csv')} disabled={isExporting}>
                  <FileText className="mr-2 h-4 w-4" />
                  csv
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('xlsx')} disabled={isExporting}>
                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                  excel
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('json')} disabled={isExporting}>
                  <FileCode className="mr-2 h-4 w-4" />
                  json
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Sensors List - Grid or Table View */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Skeleton className="h-[250px] w-full rounded-md" />
          <Skeleton className="h-[250px] w-full rounded-md" />
        </div>
      ) : filteredSensors.length === 0 ? (
        <div className="col-span-2 text-center py-8 text-muted-foreground">
          {sensors.length === 0 ? 'No sensors connected to this device' : 'No sensors match the selected filters'}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredSensors.map((sensor) => {
            // Find stats for this sensor (filtered/aggregated)
            const stats = sensorStats.find((s) => s.sensor_id === sensor.id);

            // Find latest reading from sensor.latest_data
            const latestData = sensor.latest_data;

            // Aggregated value for filter
            let displayValue = '-';
            let displayTimestamp = null;
            if (stats && stats.value !== null && stats.value !== undefined) {
              displayValue = Number(stats.value).toFixed(1);
              displayTimestamp = stats.timestamp;
            }

            // Latest value (regardless of filter)
            let latestValue = '-';
            let latestTimestamp = null;
            if (latestData && latestData.value !== null && latestData.value !== undefined) {
              latestValue = Number(latestData.value).toFixed(1);
              latestTimestamp = latestData.timestamp;
            }

            const unit = sensor.unit || stats?.unit || '';

            // Get the stats label based on current aggregation
            let statsLabel = 'Latest Reading';
            switch (aggregation) {
              case 'average':
                statsLabel = 'Average';
                break;
              case 'min':
                statsLabel = 'Minimum';
                break;
              case 'max':
                statsLabel = 'Maximum';
                break;
            }

            return (
              <Card key={sensor.id} className="overflow-hidden">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle>{sensor.name || `${sensor.type} Sensor`}</CardTitle>
                      <div className="flex mt-1">
                        <Badge variant="secondary">{sensor.type}</Badge>
                      </div>
                    </div>
                    <div className="text-2xl font-semibold">
                      {displayValue} <span className="text-sm font-normal">{unit}</span>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="pb-2">
                  <div className="h-[150px] flex flex-col items-center justify-center gap-2">
                    {/* Aggregated value for filter */}
                    <div className="text-center">
                      <div className="text-3xl font-bold">
                        {displayValue}
                        <span className="text-base font-normal ml-1">{unit}</span>
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        {statsLabel} for {selectedTimeRangeLabel}
                      </div>
                      {displayTimestamp && (
                        <div className="text-xs text-muted-foreground mt-1">
                          Aggregated: {new Date(displayTimestamp).toLocaleString()}
                        </div>
                      )}
                    </div>
                    {/* Divider */}
                    <Separator className="my-2" />
                    {/* Latest value (regardless of filter) */}
                    <div className="text-center">
                      <div className="text-lg font-medium">
                        Last Reading: {latestValue}
                        <span className="text-base font-normal ml-1">{unit}</span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {latestTimestamp ? `Recorded: ${new Date(latestTimestamp).toLocaleString()}` : 'No recent data'}
                      </div>
                    </div>
                  </div>
                </CardContent>

                <Separator />

                <CardFooter className="pt-4 pb-3">
                  <div className="w-full flex justify-between items-center px-1">
                    <span className="text-sm text-muted-foreground">
                      {selectedTimeRangeLabel} • {statsLabel}
                    </span>
                    <div className="flex gap-2">
                      <Link
                        to="/dashboard/devices/$deviceId"
                        params={{ deviceId: deviceId }}
                        search={{ tab: 'analytics' }}
                        className="text-sm text-muted-foreground font-medium hover:text-primary mt-1 block"
                      >
                        View Analytics
                      </Link>
                      <UpdateSensor deviceId={deviceId} sensor={sensor} refetch={refetch} />
                      <DeleteSensor deviceId={deviceId} sensor={sensor} refetch={refetch} />
                    </div>
                  </div>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      ) : (
        // Table View
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Sensor</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>
                  {aggregation === 'latest'
                    ? 'Filtered Value'
                    : aggregation === 'average'
                      ? 'Average Value'
                      : aggregation === 'min'
                        ? 'Min Value'
                        : 'Max Value'}
                </TableHead>
                <TableHead>Unit</TableHead>
                {aggregation === 'latest' && <TableHead>Last Update</TableHead>}
                <TableHead>Period</TableHead>
                <TableHead>Last Reading</TableHead>
                <TableHead>Last Reading Time</TableHead>
                <TableHead className="w-[100px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSensors.map((sensor) => {
                const stats = sensorStats.find((s) => s.sensor_id === sensor.id);
                const latestData = sensor.latest_data;

                // Aggregated value for filter
                let displayValue = '-';
                let displayTimestamp = null;
                if (stats && stats.value !== null && stats.value !== undefined) {
                  displayValue = Number(stats.value).toFixed(1);
                  displayTimestamp = stats.timestamp;
                }

                // Latest value (regardless of filter)
                let latestValue = '-';
                let latestTimestamp = null;
                if (latestData && latestData.value !== null && latestData.value !== undefined) {
                  latestValue = Number(latestData.value).toFixed(1);
                  latestTimestamp = latestData.timestamp;
                }

                const unit = sensor.unit || stats?.unit || '';

                return (
                  <TableRow key={sensor.id}>
                    <TableCell className="font-medium">{sensor.name || `Unnamed Sensor`}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{sensor.type}</Badge>
                    </TableCell>
                    <TableCell className="font-semibold">{displayValue}</TableCell>
                    <TableCell>{unit}</TableCell>
                    {aggregation === 'latest' && (
                      <TableCell className="text-muted-foreground text-sm">
                        {displayTimestamp ? new Date(displayTimestamp).toLocaleString() : 'No data'}
                      </TableCell>
                    )}
                    <TableCell className="text-muted-foreground">{selectedTimeRangeLabel}</TableCell>
                    <TableCell className="font-semibold">{latestValue}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {latestTimestamp ? new Date(latestTimestamp).toLocaleString() : 'No data'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <UpdateSensor deviceId={deviceId} sensor={sensor} refetch={refetch} />
                        <DeleteSensor deviceId={deviceId} sensor={sensor} refetch={refetch} />
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
